/**
 * CSS Variables Theme System
 * 
 * This module provides utilities for converting Material-UI theme objects
 * to CSS custom properties (variables) and applying them to the document root.
 * 
 * Features:
 * - Converts MUI theme colors to CSS variables
 * - Supports all 8 predefined themes
 * - Optimizes performance by avoiding re-renders on theme changes
 * - Provides smooth transitions between themes
 * - Maintains backward compatibility with Material-UI theme system
 */

/**
 * Flattens a nested object into dot-notation keys
 * Example: { palette: { primary: { main: '#000' } } } 
 * becomes { 'palette-primary-main': '#000' }
 */
const flattenObject = (obj, prefix = '', separator = '-') => {
  const flattened = {};
  
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      const newKey = prefix ? `${prefix}${separator}${key}` : key;
      
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        Object.assign(flattened, flattenObject(value, newKey, separator));
      } else {
        flattened[newKey] = value;
      }
    }
  }
  
  return flattened;
};

/**
 * Converts a Material-UI theme object to CSS custom properties
 * @param {object} theme - Material-UI theme object
 * @param {string} prefix - CSS variable prefix (default: '--theme')
 * @returns {object} Object with CSS variable names as keys and values
 */
export const convertThemeToCSSVariables = (theme, prefix = '--theme') => {
  const cssVariables = {};
  
  // Extract key theme properties that we want as CSS variables
  const extractableProperties = {
    palette: theme.palette,
    spacing: theme.spacing ? {
      unit: theme.spacing(1),
      xs: theme.spacing(0.5),
      sm: theme.spacing(1),
      md: theme.spacing(2),
      lg: theme.spacing(3),
      xl: theme.spacing(4),
    } : {},
    shape: theme.shape,
    breakpoints: theme.breakpoints ? {
      xs: theme.breakpoints.values.xs,
      sm: theme.breakpoints.values.sm,
      md: theme.breakpoints.values.md,
      lg: theme.breakpoints.values.lg,
      xl: theme.breakpoints.values.xl,
    } : {},
    shadows: theme.shadows ? {
      none: theme.shadows[0],
      sm: theme.shadows[1],
      md: theme.shadows[4],
      lg: theme.shadows[8],
      xl: theme.shadows[16],
    } : {},
    transitions: theme.transitions ? {
      duration: theme.transitions.duration.standard || '300ms',
      easing: theme.transitions.easing.easeInOut || 'cubic-bezier(0.4, 0, 0.2, 1)',
    } : {},
  };
  
  // Flatten the extractable properties
  const flattened = flattenObject(extractableProperties);
  
  // Convert to CSS variable format
  Object.entries(flattened).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      // Convert camelCase to kebab-case
      const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
      const cssVarName = `${prefix}-${cssKey}`;
      
      // Handle different value types
      let cssValue;
      if (typeof value === 'number') {
        cssValue = `${value}px`;
      } else if (typeof value === 'string') {
        cssValue = value;
      } else {
        cssValue = String(value);
      }
      
      cssVariables[cssVarName] = cssValue;
    }
  });
  
  return cssVariables;
};

/**
 * Applies CSS variables to the document root element
 * @param {object} cssVariables - Object with CSS variable names and values
 * @param {Element} element - Target element (default: document.documentElement)
 */
export const applyCSSVariablesToRoot = (cssVariables, element = document.documentElement) => {
  // Clear existing theme variables first (optional, for clean transitions)
  const existingVars = Array.from(element.style).filter(prop => prop.startsWith('--theme'));
  existingVars.forEach(varName => {
    element.style.removeProperty(varName);
  });
  
  // Apply new variables
  Object.entries(cssVariables).forEach(([varName, value]) => {
    element.style.setProperty(varName, value);
  });
};

/**
 * Creates CSS transition rules for smooth theme changes
 * @param {string} duration - Transition duration (default: '300ms')
 * @param {string} easing - Transition timing function
 * @returns {string} CSS transition rule
 */
export const createThemeTransitionCSS = (duration = '300ms', easing = 'ease-in-out') => {
  const transitionProperties = [
    'background-color',
    'color',
    'border-color',
    'box-shadow',
    'opacity',
  ];
  
  return transitionProperties.map(prop => `${prop} ${duration} ${easing}`).join(', ');
};

/**
 * Injects theme transition styles into the document head
 * This ensures smooth transitions between theme changes
 */
export const injectThemeTransitionStyles = () => {
  const existingStyle = document.getElementById('theme-transitions');
  if (existingStyle) {
    return; // Already injected
  }
  
  const style = document.createElement('style');
  style.id = 'theme-transitions';
  style.textContent = `
    :root {
      color-scheme: light dark;
    }
    
    * {
      transition: ${createThemeTransitionCSS()};
    }
    
    /* Disable transitions for elements that shouldn't animate */
    *:focus,
    *:hover,
    .MuiSlider-thumb,
    .MuiSwitch-thumb,
    .MuiCheckbox-root,
    .MuiRadio-root {
      transition: none;
    }
    
    /* Chart.js canvas should not transition */
    canvas {
      transition: none !important;
    }
    
    /* Performance optimization for large lists */
    .MuiList-root > *,
    .MuiTable-root > * > * {
      transition: background-color 300ms ease-in-out, color 300ms ease-in-out;
    }
  `;
  
  document.head.appendChild(style);
};

/**
 * Performance monitoring for theme changes
 */
export const performanceMonitor = {
  startTime: 0,

  start() {
    this.startTime = performance.now();
  },

  end(operation = 'theme-change') {
    const duration = performance.now() - this.startTime;
    if (duration > 16) { // More than one frame (60fps)
      console.warn(`Theme operation '${operation}' took ${duration.toFixed(2)}ms`);
    }
    return duration;
  },
};

const CSSVariablesUtils = {
  convertThemeToCSSVariables,
  applyCSSVariablesToRoot,
  createThemeTransitionCSS,
  injectThemeTransitionStyles,
  performanceMonitor,
};

export default CSSVariablesUtils;